import { IntlShape } from 'react-intl';

/**
 * What a KPI tile shows under its value.
 *
 * - `delta`: a change against the period before. `value` drives the colour, `label` is the text.
 * - `firstWeek`: the value has less history than the period, so no change is shown (design a2).
 * - `none`: nothing to show.
 */
export type HomeTileDelta = { kind: 'delta'; value: number; label: string } | { kind: 'firstWeek' } | { kind: 'none' };

/** The percent change from `previous` to `current`. Null when either is unknown or `previous` is 0. */
export function percentChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

/** "+1,204", "−12", "0". */
export function formatSignedCount(intl: IntlShape, value: number): string {
  return intl.formatNumber(value, { signDisplay: 'exceptZero' });
}

/** "+8.4%", "−3%". `value` is already in percent (8.4, not 0.084). */
export function formatSignedPercent(intl: IntlShape, value: number): string {
  return intl.formatNumber(value / 100, { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' });
}

/** A big value reads short: 1,310,000 → "1.31M". Below a million the full number. */
export function formatKpiValue(intl: IntlShape, value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 2 });
  }
  return intl.formatNumber(value);
}

/**
 * The delta of a count that a reading gives as a difference (followers, monthly listeners): the
 * server sends `periodDelta`, which is null when no reading is as old as the period.
 */
export function countDelta(intl: IntlShape, latest: number | null, periodDelta: number | null): HomeTileDelta {
  if (latest === null) {
    return { kind: 'none' };
  }
  if (periodDelta === null) {
    return { kind: 'firstWeek' };
  }
  return { kind: 'delta', value: periodDelta, label: formatSignedCount(intl, periodDelta) };
}

/**
 * The delta of a flow summed over the period (streams, views, clicks) against the period before.
 * No earlier activity at all reads as "first week", not as an endless percent.
 */
export function flowDelta(intl: IntlShape, current: number | null, previous: number | null): HomeTileDelta {
  if (current === null) {
    return { kind: 'none' };
  }
  if (previous === null || previous === 0) {
    return current === 0 ? { kind: 'none' } : { kind: 'firstWeek' };
  }
  const change = percentChange(current, previous) ?? 0;
  return { kind: 'delta', value: change, label: formatSignedPercent(intl, change) };
}

/** The change of a rate in percentage points, to one decimal: 33.2 vs 34.1 → −0.9. Null when unknown. */
export function pointChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) {
    return null;
  }
  return Math.round((current - previous) * 10) / 10;
}
